// src/pages/finance-portal/tax-settings/TaxSettings.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Switch, Card,
    FormControlLabel, MenuItem, Alert, CircularProgress, Grid, Chip,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const cardSx = {
    p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', mb: 3,
};

const inputSx = {
    '& .MuiOutlinedInput-root': { fontFamily, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily },
};

const ENTITY_TYPES = [
    { value: 'SOLE_PROPRIETORSHIP', label: 'Sole Proprietorship' },
    { value: 'PARTNERSHIP', label: 'Partnership' },
    { value: 'LLC', label: 'LLC' },
    { value: 'S_CORP', label: 'S Corporation' },
    { value: 'C_CORP', label: 'C Corporation' },
    { value: 'NON_PROFIT', label: 'Non-Profit' },
];

const INCOME_TAX_METHODS = [
    { value: 'CASH', label: 'Cash Basis' },
    { value: 'ACCRUAL', label: 'Accrual Basis' },
];

const DEPRECIATION_METHODS = [
    { value: 'STRAIGHT_LINE', label: 'Straight Line' },
    { value: 'DECLINING_BALANCE', label: 'Declining Balance' },
    { value: 'MACRS', label: 'MACRS' },
    { value: 'SECTION_179', label: 'Section 179' },
];

const ESTIMATED_TAX_METHODS = [
    { value: 'SAFE_HARBOR_PRIOR', label: 'Safe Harbor (Prior Year)' },
    { value: 'SAFE_HARBOR_CURRENT', label: 'Safe Harbor (Current Year)' },
    { value: 'ANNUALIZED', label: 'Annualized Income' },
];

const MONTHS = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
    'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
    'VA','WA','WV','WI','WY','DC',
];

export default function TaxSettings() {
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [glAccounts, setGlAccounts] = useState([]);

    const [settings, setSettings] = useState({
        entity_type: 'LLC',
        fiscal_year_end_month: 12,
        federal_tax_id: '',
        income_tax_method: 'ACCRUAL',
        depreciation_method: 'STRAIGHT_LINE',
        default_sales_tax_rate: '',
        auto_calculate_sales_tax: false,
        nexus_states: [],
        estimated_tax_method: 'SAFE_HARBOR_PRIOR',
        prior_year_tax_liability: '',
        prior_year_agi: '',
        qbi_eligible: false,
        tax_expense_account_id: '',
        tax_payable_account_id: '',
        sales_tax_payable_account_id: '',
        deferred_tax_asset_account_id: '',
        deferred_tax_liability_account_id: '',
    });

    const [nexusInput, setNexusInput] = useState('');

    const fetchSettings = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/tax/settings');
            if (res.data.success && res.data.data) {
                const d = res.data.data;
                setSettings({
                    entity_type: d.entity_type || 'LLC',
                    fiscal_year_end_month: d.fiscal_year_end_month || 12,
                    federal_tax_id: d.federal_tax_id || '',
                    income_tax_method: d.income_tax_method || 'ACCRUAL',
                    depreciation_method: d.depreciation_method || 'STRAIGHT_LINE',
                    default_sales_tax_rate: d.default_sales_tax_rate ?? '',
                    auto_calculate_sales_tax: d.auto_calculate_sales_tax || false,
                    nexus_states: d.nexus_states || [],
                    estimated_tax_method: d.estimated_tax_method || 'SAFE_HARBOR_PRIOR',
                    prior_year_tax_liability: d.prior_year_tax_liability ?? '',
                    prior_year_agi: d.prior_year_agi ?? '',
                    qbi_eligible: d.qbi_eligible || false,
                    tax_expense_account_id: d.tax_expense_account_id || '',
                    tax_payable_account_id: d.tax_payable_account_id || '',
                    sales_tax_payable_account_id: d.sales_tax_payable_account_id || '',
                    deferred_tax_asset_account_id: d.deferred_tax_asset_account_id || '',
                    deferred_tax_liability_account_id: d.deferred_tax_liability_account_id || '',
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load tax settings');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchGlAccounts = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
            if (res.data.success) {
                setGlAccounts(res.data.data || []);
            }
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { fetchSettings(); fetchGlAccounts(); }, [fetchSettings, fetchGlAccounts]);

    const handleChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleAddNexusState = () => {
        const state = nexusInput.toUpperCase().trim();
        if (state && US_STATES.includes(state) && !settings.nexus_states.includes(state)) {
            setSettings(prev => ({ ...prev, nexus_states: [...prev.nexus_states, state] }));
        }
        setNexusInput('');
    };

    const handleRemoveNexusState = (state) => {
        setSettings(prev => ({ ...prev, nexus_states: prev.nexus_states.filter(s => s !== state) }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                ...settings,
                default_sales_tax_rate: settings.default_sales_tax_rate !== '' ? parseFloat(settings.default_sales_tax_rate) : null,
                prior_year_tax_liability: settings.prior_year_tax_liability !== '' ? parseFloat(settings.prior_year_tax_liability) : null,
                prior_year_agi: settings.prior_year_agi !== '' ? parseFloat(settings.prior_year_agi) : null,
                tax_expense_account_id: settings.tax_expense_account_id || null,
                tax_payable_account_id: settings.tax_payable_account_id || null,
                sales_tax_payable_account_id: settings.sales_tax_payable_account_id || null,
                deferred_tax_asset_account_id: settings.deferred_tax_asset_account_id || null,
                deferred_tax_liability_account_id: settings.deferred_tax_liability_account_id || null,
            };
            await axiosInstance.put('/api/finance/tax/settings', payload);
            setSuccess('Tax settings saved successfully');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save tax settings');
        } finally {
            setSaving(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <SettingsIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Tax Settings</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Configure tax calculation and compliance settings</Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: ACCENT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        {saving ? <CircularProgress size={20} sx={{ color: ACCENT }} /> : 'Save Settings'}
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1100, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Business Entity */}
                <Card elevation={0} sx={cardSx}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2.5, color: '#1e293b' }}>Business Entity</Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth select label="Entity Type"
                                value={settings.entity_type}
                                onChange={e => handleChange('entity_type', e.target.value)}
                                sx={inputSx}>
                                {ENTITY_TYPES.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth select label="Fiscal Year End Month"
                                value={settings.fiscal_year_end_month}
                                onChange={e => handleChange('fiscal_year_end_month', parseInt(e.target.value, 10))}
                                sx={inputSx}>
                                {MONTHS.map(m => <MenuItem key={m.value} value={m.value} sx={{ fontFamily }}>{m.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth label="Federal Tax ID (EIN)"
                                value={settings.federal_tax_id}
                                onChange={e => handleChange('federal_tax_id', e.target.value)}
                                placeholder="XX-XXXXXXX"
                                sx={inputSx} />
                        </Grid>
                    </Grid>
                </Card>

                {/* Tax Method */}
                <Card elevation={0} sx={cardSx}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2.5, color: '#1e293b' }}>Tax Method</Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <TextField size="small" fullWidth select label="Income Tax Method"
                                value={settings.income_tax_method}
                                onChange={e => handleChange('income_tax_method', e.target.value)}
                                sx={inputSx}>
                                {INCOME_TAX_METHODS.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField size="small" fullWidth select label="Depreciation Method"
                                value={settings.depreciation_method}
                                onChange={e => handleChange('depreciation_method', e.target.value)}
                                sx={inputSx}>
                                {DEPRECIATION_METHODS.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>
                </Card>

                {/* Sales Tax Configuration */}
                <Card elevation={0} sx={cardSx}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2.5, color: '#1e293b' }}>Sales Tax Configuration</Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth label="Default Sales Tax Rate (%)" type="number"
                                value={settings.default_sales_tax_rate}
                                onChange={e => handleChange('default_sales_tax_rate', e.target.value)}
                                inputProps={{ step: 0.001, min: 0 }}
                                sx={inputSx} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.auto_calculate_sales_tax}
                                        onChange={e => handleChange('auto_calculate_sales_tax', e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT },
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontWeight: 600 }}>Auto-Calculate Sales Tax</Typography>}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', mb: 1, color: '#475569' }}>Nexus States</Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                {settings.nexus_states.map(state => (
                                    <Chip key={state} label={state} size="small" onDelete={() => handleRemoveNexusState(state)}
                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', bgcolor: '#dcfce7', color: '#166534' }} />
                                ))}
                                {settings.nexus_states.length === 0 && (
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>No nexus states configured</Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField size="small" select label="Add State" value={nexusInput}
                                    onChange={e => setNexusInput(e.target.value)}
                                    sx={{ ...inputSx, minWidth: 120 }}>
                                    <MenuItem value="" sx={{ fontFamily }}>Select...</MenuItem>
                                    {US_STATES.filter(s => !settings.nexus_states.includes(s)).map(s => (
                                        <MenuItem key={s} value={s} sx={{ fontFamily }}>{s}</MenuItem>
                                    ))}
                                </TextField>
                                <Button onClick={handleAddNexusState} variant="outlined" size="small" disabled={!nexusInput}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', borderColor: ACCENT, color: ACCENT, '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' } }}>
                                    Add
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>

                {/* Estimated Tax */}
                <Card elevation={0} sx={cardSx}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2.5, color: '#1e293b' }}>Estimated Tax</Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <TextField size="small" fullWidth select label="Estimated Tax Method"
                                value={settings.estimated_tax_method}
                                onChange={e => handleChange('estimated_tax_method', e.target.value)}
                                sx={inputSx}>
                                {ESTIMATED_TAX_METHODS.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField size="small" fullWidth label="Prior Year Tax Liability" type="number"
                                value={settings.prior_year_tax_liability}
                                onChange={e => handleChange('prior_year_tax_liability', e.target.value)}
                                sx={inputSx} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField size="small" fullWidth label="Prior Year AGI" type="number"
                                value={settings.prior_year_agi}
                                onChange={e => handleChange('prior_year_agi', e.target.value)}
                                sx={inputSx} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.qbi_eligible}
                                        onChange={e => handleChange('qbi_eligible', e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT },
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontWeight: 600 }}>QBI Deduction Eligible</Typography>}
                            />
                        </Grid>
                    </Grid>
                </Card>

                {/* GL Accounts */}
                <Card elevation={0} sx={cardSx}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2.5, color: '#1e293b' }}>GL Account Mapping</Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <TextField size="small" fullWidth select label="Tax Expense Account"
                                value={settings.tax_expense_account_id}
                                onChange={e => handleChange('tax_expense_account_id', e.target.value)}
                                sx={inputSx}>
                                <MenuItem value="" sx={{ fontFamily }}>-- Select --</MenuItem>
                                {glAccounts.map(a => <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>{a.account_number} - {a.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField size="small" fullWidth select label="Tax Payable Account"
                                value={settings.tax_payable_account_id}
                                onChange={e => handleChange('tax_payable_account_id', e.target.value)}
                                sx={inputSx}>
                                <MenuItem value="" sx={{ fontFamily }}>-- Select --</MenuItem>
                                {glAccounts.map(a => <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>{a.account_number} - {a.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth select label="Sales Tax Payable Account"
                                value={settings.sales_tax_payable_account_id}
                                onChange={e => handleChange('sales_tax_payable_account_id', e.target.value)}
                                sx={inputSx}>
                                <MenuItem value="" sx={{ fontFamily }}>-- Select --</MenuItem>
                                {glAccounts.map(a => <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>{a.account_number} - {a.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth select label="Deferred Tax Asset Account"
                                value={settings.deferred_tax_asset_account_id}
                                onChange={e => handleChange('deferred_tax_asset_account_id', e.target.value)}
                                sx={inputSx}>
                                <MenuItem value="" sx={{ fontFamily }}>-- Select --</MenuItem>
                                {glAccounts.map(a => <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>{a.account_number} - {a.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth select label="Deferred Tax Liability Account"
                                value={settings.deferred_tax_liability_account_id}
                                onChange={e => handleChange('deferred_tax_liability_account_id', e.target.value)}
                                sx={inputSx}>
                                <MenuItem value="" sx={{ fontFamily }}>-- Select --</MenuItem>
                                {glAccounts.map(a => <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>{a.account_number} - {a.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>
                </Card>

                {/* Save Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' }, px: 5, py: 1.2,
                        }}
                    >
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Settings'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
